import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  ViewContainerRef,
} from '@angular/core';
import {AbstractControl, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';
import {
  Mask,
  MaskDirective,
  MaskElementDirective,
  MaskValidatorConfig,
  MaskValidatorMethodMap,
} from '../interfaces/mask';
import {CustomMaskComponent} from './component/custom-mask/custom-mask.component';
import {CustomMaskInputs} from './enums/custom-mask-inputs';
import {CustomDateMask, CustomMaskRegex} from './enums/custom-mask-regex';
import {dateRegex} from './static/staticValues';
import {MaskValidator} from './validators/mask-validator/mask-validator';

@Directive({
    selector: '[customMask]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: CustomMaskDirective,
            multi: true,
        },
    ],
    standalone: false
})
export class CustomMaskDirective implements Validator, AfterViewInit {
  private _customMask?: Mask<keyof MaskValidatorMethodMap>;
  @Input() set customMask(val: Mask<keyof MaskValidatorMethodMap>) {
    if (val) {
      this._customMask = val;
      this.setMaskData();
      if (val.maskText) this.updateMaskComponentsInput(CustomMaskInputs.MASK, val.maskText);
    }
  }
  get customMask() {
    return this._customMask!;
  }
  @Input() alignRight: boolean = false;

  private component!: ComponentRef<CustomMaskComponent>;
  private validateMask: boolean = false;
  private arrMasks: MaskElementDirective[] = [];

  private oldValue: string = '';
  private control!: AbstractControl;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
  ) {}

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    this.control = control;

    if (val && this.arrMasks.length > 0 && this.customMask) {
      const maskValidator: MaskValidator = new MaskValidator(
        control,
        this.arrMasks,
        this.validateMask,
      );

      const fnStr: MaskValidatorConfig<keyof MaskValidatorMethodMap> =
        typeof this.customMask.validator === 'function'
          ? (this.customMask.validator as Function)()
          : this.customMask.validator || MaskValidator.standard();
      if (fnStr.args) {
        fnStr.args['isDeleting'] = this.oldValue.length > val.length ? this.oldValue : '';
      }
      const res = maskValidator.call(fnStr);
      this.oldValue = res.text || '';

      if (res.text) {
        this.updateMaskComponentsInput(CustomMaskInputs.TEXT, res.text);
      }
      if (typeof res.mask === 'string') {
        this.updateMaskComponentsInput(CustomMaskInputs.MASK, res.mask);
      }

      return res.error;
    } else {
      this.updateMaskComponentsInput(CustomMaskInputs.TEXT, '');
      if (this.arrMasks[0]) {
        this.updateMaskComponentsInput(
          CustomMaskInputs.MASK,
          (<Mask<keyof MaskValidatorMethodMap>>this.customMask)?.maskText ||
            this.arrMasks[0].maskText,
        );
      }
    }

    this.oldValue = val || '';
    return null;
  }

  ngAfterViewInit(): void {
    if (this.customMask) {
      this.initMask();
    }
  }

  setCssProperties() {
    if (typeof window === 'object') {
      const parentElement = this.elementRef.nativeElement.parentElement as HTMLElement;
      const style = window?.getComputedStyle(parentElement);
      const elStyle = window?.getComputedStyle(this.elementRef.nativeElement);
      if (['static', 'unset', 'inherit'].indexOf(style.position) >= 0) {
        parentElement.style.position = 'relative';
      }
      if (!(parseInt(elStyle.zIndex) > 0)) {
        this.elementRef.nativeElement.style.zIndex = '1';
      }
      if (['static', 'unset', 'inherit'].indexOf(elStyle.position) >= 0) {
        this.elementRef.nativeElement.style.position = 'relative';
      }

      this.component.instance.alignRight = this.alignRight;
    }
  }
  setFocusActions() {
    this.elementRef.nativeElement.onfocus = (_el) => {
      this.updateMaskComponentsInput(CustomMaskInputs.FOCUS, true);
      MaskValidator.args['blur'] = false;
      this.control.updateValueAndValidity({onlySelf: true, emitEvent: false});
    };

    this.elementRef.nativeElement.onblur = (_el) => {
      this.updateMaskComponentsInput(CustomMaskInputs.FOCUS, false);
      MaskValidator.args['blur'] = true;
      this.control.updateValueAndValidity({onlySelf: true, emitEvent: true});
    };
  }

  updateMaskComponentsInput(key: CustomMaskInputs, value: unknown) {
    if (this.component?.instance) {
      const instance: InstanceType<any> = this.component.instance;
      instance[key] = value;
    }
  }

  stringToRegex(string: string): (RegExp | string)[] {
    if (CustomDateMask[string as any]) {
      return string.split(dateRegex).reduce((arr: any, el: string) => {
        if (el && el.length > 0) {
          arr.push(el);
        }
        return arr;
      }, []);
    }
    return string.split('').reduce((arr: (RegExp | string)[], el) => {
      if (el) {
        const s: any = el.toString() === '0' ? 'Z' : el.toString();
        const regex: string = CustomMaskRegex[s] || s;
        const r = CustomMaskRegex[s] ? new RegExp(regex as string) : regex;
        arr.push(r);
      }
      return arr;
    }, []);
  }

  setCustomMaskObj(
    mask: Mask<keyof MaskValidatorMethodMap>,
  ): MaskDirective<keyof MaskValidatorMethodMap> {
    const arrMasks = (
      Array.isArray(mask.masks)
        ? mask.masks
        : [{mask: mask.masks, maskText: (mask as any).maskText}]
    ).map((el: any) => {
      const mask = el.mask || el;
      const maskText = el.maskText || (typeof mask === 'string' ? mask : null);

      return {
        mask: typeof mask === 'string' ? this.stringToRegex(mask) : mask,
        maskText,
      };
    });

    return {
      ...mask,
      masks: arrMasks,
    };
  }
  initMask() {
    this.component = this.viewContainerRef.createComponent(CustomMaskComponent);
    this.setFocusActions();
    this.setCssProperties();
    this.setMaskData();
  }
  setMaskData() {
    const {masks, validateMask} = this.setCustomMaskObj(this.customMask);
    this.validateMask = validateMask || this.validateMask;
    this.arrMasks = masks;
  }
}
